import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { RefreshCw, Eye } from 'lucide-react';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const MessageHistoryCard = forwardRef((props, ref) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [hoveredResponseId, setHoveredResponseId] = useState(null);
  const [filterPopupPosition, setFilterPopupPosition] = useState({ top: 0, left: 0 });
  const [responsePopupPosition, setResponsePopupPosition] = useState({ top: 0, left: 0 });
  const [downloadingCSV, setDownloadingCSV] = useState(false);
  const itemsPerPage = 10;

  // Expose refresh function to parent
  useImperativeHandle(ref, () => ({
    refreshHistory: () => {
      fetchCampaigns();
    }
  }));

  useEffect(() => {
    fetchCampaigns();
  }, [currentPage]);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("superadmintoken");
      const response = await axios.get(
        `${BASE_URL}/lenskart-admin/getMessageHistory?page=${currentPage}&limit=${itemsPerPage}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      
      if (response.data.success) {
        setCampaigns(response.data.history || []);
        setTotalPages(response.data.pagination?.totalPages || 1);
      } else {
        setCampaigns([]);
      }
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      setCampaigns([]);
      toast.error("Failed to load message history");
    } finally {
      setLoading(false);
    }
  };

  // If the message type isn't explicitly push_notification, default to WhatsApp
  const getMessageTypeLabel = (type) => {
    return type === 'push_notification' ? 'Push Notification' : 'WhatsApp';
  };

  const getMessageTypeBadge = (type) => {
  const typeConfig = {
    'whatsapp': { label: 'WhatsApp', color: 'bg-emerald-100 text-emerald-800' },
    'push_notification': { label: 'Push Notification', color: 'bg-blue-100 text-blue-800' },
  };

  const config = typeConfig[type] || typeConfig['whatsapp'];

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
};

  const handleDownloadCSV = async (campaign) => {
    if (!campaign) {
      toast.error("No campaign selected for download.");
      return;
    }

    const token = localStorage.getItem("superadmintoken");
    
    try {
      setDownloadingCSV(true);
      const response = await axios.post(
        `${BASE_URL}/lenskart-admin/getWorkersByResponse`,
        {
          campaignId: campaign._id
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          responseType: 'blob' // Important for file download
        }
      );

      // Create a blob from the response data
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Create filename with location, target date, template name, and response type
      const location = campaign.filters?.location || 'unknown_location';
      const targetDate = campaign.targetedDate || 'unknown_date';
      const templateName = campaign.templateName || 'unknown_template';
      
      // Format the filename: location_targetdate_templatename_responses.csv
      const formattedLocation = location.replace(/\s+/g, '_').toLowerCase();
      const formattedTargetDate = targetDate.replace(/\s+/g, '_').toLowerCase();
      const formattedTemplateName = templateName.replace(/\s+/g, '_').toLowerCase();
      
      const filename = `${formattedLocation}_${formattedTargetDate}_${formattedTemplateName}_responses.csv`;
      
      link.setAttribute('download', filename);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("CSV downloaded successfully");
    } catch (error) {
      console.error("Error downloading CSV:", error);
      toast.error("Failed to download CSV");
    } finally {
      setDownloadingCSV(false);
    }
  };

  const handleResponseClick = (campaign) => {
    handleDownloadCSV(campaign);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'completed': 'bg-green-100 text-green-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'failed': 'bg-red-100 text-red-800',
      'processing': 'bg-blue-100 text-blue-800'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800'}`}>
        {status || 'N/A'}
      </span>
    );
  };

  const formatFilterValue = (key, value) => {
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return value || 'N/A';
  };

  const handleFilterHover = (e, campaignId) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setFilterPopupPosition({
      top: rect.top - 10,
      left: rect.left + rect.width / 2
    });
    setHoveredRowId(campaignId);
  };

  const handleResponseHover = (e, campaignId) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setResponsePopupPosition({
      top: rect.top - 10,
      left: rect.left + rect.width / 2
    });
    setHoveredResponseId(campaignId);
  };

  const FilterPopup = ({ filters }) => {
    if (!filters) return null;

    return (
      <div 
        className="fixed z-50 bg-white border-2 border-gray-300 rounded-lg shadow-xl p-4 min-w-[300px] max-w-[400px]"
        style={{
          top: `${filterPopupPosition.top}px`,
          left: `${filterPopupPosition.left}px`,
          transform: 'translate(-50%, -100%)'
        }}
      >
        <div className="space-y-2">
          <h4 className="font-semibold text-sm text-gray-900 border-b pb-2">Filter Criteria</h4>
          {Object.entries(filters).map(([key, value]) => (
            <div key={key} className="text-xs">
              <span className="font-medium text-gray-700 capitalize">{key.replace('_', ' ')}: </span>
              <span className="text-gray-600">{formatFilterValue(key, value)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const ResponsePopup = ({ totalResponses }) => {
    if (!totalResponses || !totalResponses.count) return null;

    return (
      <div 
        className="fixed z-50 bg-white border-2 border-gray-300 rounded-lg shadow-xl p-4 min-w-[200px] max-w-[300px]"
        style={{
          top: `${responsePopupPosition.top}px`,
          left: `${responsePopupPosition.left}px`,
          transform: 'translate(-50%, -100%)'
        }}
      >
        <div className="space-y-2">
          <h4 className="font-semibold text-sm text-gray-900 border-b pb-2">Response Details</h4>
          {/* <div className="text-xs">
            <span className="font-medium text-gray-700">Total Count: </span>
            <span className="text-gray-600">{totalResponses.count}</span>
          </div> */}
          {Object.entries(totalResponses).map(([key, value]) => {
            if (key !== 'count') {
              return (
                <div key={key} className="text-xs">
                  <span className="font-medium text-gray-700 capitalize">{key}: </span>
                  <span className="text-gray-600">{value}</span>
                </div>
              );
            }
            return null;
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-md w-full">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold text-gray-800">Message Send History</h3>
        <button
          onClick={fetchCampaigns}
          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SNo.</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Send On</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sent By</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Filter Criteria</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Msg/ Sent</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Msg Delivered/ Read</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Responses</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {campaigns && campaigns.length > 0 ? (
              campaigns.map((campaign, index) => (
                <tr key={campaign._id || index} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {(currentPage - 1) * itemsPerPage + index + 1}
                  </td>
                  <td className="px-4 py-3 text-sm">{formatDate(campaign.created_at)}</td>
                  <td className="px-4 py-3 text-sm">{getMessageTypeLabel(campaign.message_type)}</td>
                  <td className="px-4 py-3 text-sm">{campaign.adminName || 'N/A'}</td>
                  <td className="px-4 py-3 text-sm">
                    <div
                      className="flex items-center gap-1 text-blue-600 hover:text-blue-800 cursor-pointer"
                      onMouseEnter={(e) => handleFilterHover(e, campaign._id)}
                      onMouseLeave={() => setHoveredRowId(null)}
                    >
                      <Eye className="w-4 h-4" />
                      <span>View Criteria</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium">
                    <span className="text-gray-800">{campaign.TotalWorkers || 0}</span>
                    <span className="text-gray-400 mx-1">/</span>
                    <span className="text-gray-600">{campaign.TotalSuccess || 0}</span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className="text-gray-800">{campaign.messageStats?.totalDelivered || 0}</span>
                    <span className="text-gray-400 mx-1">/</span>
                    <span className="text-gray-600">{campaign.messageStats?.totalRead || 0}</span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <button
                      onClick={() => handleResponseClick(campaign)}
                      onMouseEnter={(e) => handleResponseHover(e, campaign._id)}
                      onMouseLeave={() => setHoveredResponseId(null)}
                      className="w-full text-center hover:bg-gray-100 rounded py-1 cursor-pointer font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={downloadingCSV || !campaign.totalResponses?.count}
                    >
                      {downloadingCSV ? (
                        'Downloading...'
                      ) : (
                        <span className="text-blue-600 hover:text-blue-800">
                          {campaign.totalResponses?.count || 0}
                        </span>
                      )}
                    </button>
                  </td>
                </tr>
              ))
            ) : null}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        {campaigns && campaigns.length > 0 ? (
          campaigns.map((campaign, index) => (
            <div key={campaign._id || index} className="border rounded-lg p-4 shadow-sm">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <span className="text-sm font-medium text-gray-900">SNo. {(currentPage - 1) * itemsPerPage + index + 1}</span>
                  <span className="text-sm font-medium text-gray-900 ml-4">{campaign.adminName || 'N/A'}</span>
                </div>
                {getStatusBadge(campaign.status)}
              </div>
              <div className="space-y-1 text-sm text-gray-600">
                <p><span className="font-medium">Send On:</span> {formatDate(campaign.created_at)}</p>
                <p><span className="font-medium">Message Type:</span> {getMessageTypeLabel(campaign.message_type)}</p>
                <p>
                  <span className="font-medium">Total Msg/ Sent:</span> 
                  <span className="font-medium text-gray-900 ml-1">{campaign.TotalWorkers || 0}</span>
                  <span className="text-gray-400 mx-1">/</span>
                  <span className="font-medium text-gray-600">{campaign.TotalSuccess || 0}</span>
                </p>
                <p>
                  <span className="font-medium">Msg Delivered/ Read:</span> 
                  <span className="font-medium text-gray-900 ml-1">{campaign.messageStats?.totalDelivered || 0}</span>
                  <span className="text-gray-400 mx-1">/</span>
                  <span className="font-medium text-gray-600">{campaign.messageStats?.totalRead || 0}</span>
                </p>
                <div className="mt-2">
                  <button
                    onClick={() => handleResponseClick(campaign)}
                    className="w-full text-center hover:bg-gray-100 rounded py-2 cursor-pointer font-medium transition-colors border disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={downloadingCSV || !campaign.totalResponses?.count}
                  >
                    {downloadingCSV ? (
                      'Downloading CSV...'
                    ) : (
                      <>
                        <span className="font-medium">Responses: </span>
                        <span className="text-blue-600">{campaign.totalResponses?.count || 0}</span>
                      </>
                    )}
                  </button>
                  {campaign.totalResponses && campaign.totalResponses.count > 0 && (
                    <div className="mt-2 text-xs text-gray-500">
                      <p className="font-medium">Response Details:</p>
                      {Object.entries(campaign.totalResponses).map(([key, value]) => {
                        if (key !== 'count') {
                          return (
                            <p key={key} className="ml-2">
                              <span className="font-medium capitalize">{key}: </span>
                              {value}
                            </p>
                          );
                        }
                        return null;
                      })}
                    </div>
                  )}
                </div>
                <div className="mt-2 pt-2 border-t">
                  <p className="font-medium mb-1">Filters:</p>
                  {campaign.filters && Object.entries(campaign.filters).map(([key, value]) => (
                    <p key={key} className="text-xs ml-2">
                      <span className="font-medium capitalize">{key}: </span>
                      {formatFilterValue(key, value)}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          ))
        ) : null}
      </div>

      {/* Filter Popup */}
      {hoveredRowId && (
        <FilterPopup filters={campaigns.find(c => c._id === hoveredRowId)?.filters} />
      )}

      {/* Response Popup */}
      {hoveredResponseId && (
        <ResponsePopup totalResponses={campaigns.find(c => c._id === hoveredResponseId)?.totalResponses} />
      )}

      {/* Empty State */}
      {!loading && (!campaigns || campaigns.length === 0) && (
        <div className="text-center py-8 text-gray-500">
          No message history found
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8">
          <p className="text-gray-500">Loading message history...</p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center space-x-2 mt-4">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
});

MessageHistoryCard.displayName = 'MessageHistoryCard';

export default MessageHistoryCard;